'use client'
import { motion } from 'framer-motion'
import { useLang } from '../context/LangContext'
import { t, privacy as privacyT } from '../lib/translations'

const sectionKeys = ['infoCollect', 'infoUse', 'storage', 'sharing', 'rights', 'cookies', 'security', 'changes', 'contact'] as const

const sectionContent: Record<string, { en: string; fa: string; ar: string }> = {
  infoCollect: {
    en: 'We collect information you provide directly to us, such as your name, email address, billing information, and company details when you register for an account, subscribe to a plan, or contact our support team. We also collect certain data automatically, including your IP address, browser type, operating system, referring URLs, and usage patterns across our platform.',
    fa: 'ما اطلاعاتی را که مستقیماً در اختیار ما قرار می‌دهید جمع‌آوری می‌کنیم، از جمله نام، آدرس ایمیل، اطلاعات صورتحساب و جزئیات شرکت هنگام ثبت حساب، اشتراک طرح یا تماس با تیم پشتیبانی.',
    ar: 'نجمع المعلومات التي تقدمها لنا مباشرة، مثل اسمك وعنوان بريدك الإلكتروني ومعلومات الفوترة وتفاصيل الشركة عند التسجيل للحصول على حساب.',
  },
  infoUse: {
    en: 'We use your information to provision, maintain, and improve your cloud infrastructure services; to process payments and send billing notifications; to provide technical support and respond to inquiries; to monitor and safeguard platform security; to comply with legal obligations; and to send occasional service-related announcements.',
    fa: 'ما از اطلاعات شما برای ارائه، نگهداری و بهبود خدمات زیرساخت ابری؛ پردازش پرداخت‌ها و ارسال اعلانات صورتحساب؛ ارائه پشتیبانی فنی و پاسخ به سوالات استفاده می‌کنیم.',
    ar: 'نستخدم معلوماتك لتوفير وصيانة وتحسين خدمات البنية التحتية السحابية الخاصة بك؛ ومعالجة المدفوعات وإرسال إشعارات الفوترة.',
  },
  storage: {
    en: 'Your data is stored in our data centers located in Tehran (Iran), Frankfurt (Germany), and the United States. We implement industry-standard safeguards including encryption at rest and in transit, access controls, and regular security audits.',
    fa: 'داده‌های شما در مراکز داده ما در تهران، فرانکفورت و ایالات متحده ذخیره می‌شود. ما از استانداردهای صنعتی شامل رمزگذاری در حالت سکون و انتقال استفاده می‌کنیم.',
    ar: 'يتم تخزين بياناتك في مراكز البيانات لدينا في طهران وفرانكفورت والولايات المتحدة. نطبق ضمانات على مستوى الصناعة.',
  },
  sharing: {
    en: 'We do not share your personal information with third parties except with your explicit consent, with trusted service providers under strict confidentiality agreements, when required by law, or in connection with a merger or acquisition.',
    fa: 'ما اطلاعات شخصی شما را با اشخاص ثالث به اشتراک نمی‌گذاریم، مگر با رضایت صریح شما، با ارائه‌دهندگان خدمات مورد اعتماد تحت توافقنامه‌های محرمانه، یا زمانی که قانون الزام کند.',
    ar: 'لا نشارك معلوماتك الشخصية مع أطراف ثالثة إلا بموافقتك الصريحة أو مع مقدمي الخدمات الموثوقين أو عندما يقتضي القانون ذلك.',
  },
  rights: {
    en: 'You have the right to access, correct, update, or delete your personal information at any time through your account dashboard or by contacting our support team. If you are in the EEA, you have additional rights under the GDPR.',
    fa: 'شما حق دسترسی، تصحیح، به‌روزرسانی یا حذف اطلاعات شخصی خود را در هر زمان از طریق داشبورد حساب یا تماس با تیم پشتیبانی دارید.',
    ar: 'لديك الحق في الوصول إلى معلوماتك الشخصية أو تصحيحها أو تحديثها أو حذفها في أي وقت من خلال لوحة معلومات حسابك.',
  },
  cookies: {
    en: 'We use essential cookies for authentication and session management. We also use analytics cookies (subject to consent where required) to understand platform usage and improve our services.',
    fa: 'ما از کوکی‌های ضروری برای احراز هویت و مدیریت جلسه استفاده می‌کنیم. همچنین از کوکی‌های تحلیلی برای درک استفاده از پلتفرم استفاده می‌کنیم.',
    ar: 'نستخدم الكوكيز الأساسية للمصادقة وإدارة الجلسات. نستخدم أيضًا كوكيز التحليلات لفهم استخدام المنصة.',
  },
  security: {
    en: 'We take the security of your data seriously. Our platform employs encryption (TLS 1.3) for data in transit, AES-256 for data at rest, multi-factor authentication, intrusion detection, and regular penetration testing.',
    fa: 'ما امنیت داده‌های شما را جدی می‌گیریم. پلتفرم ما از رمزگذاری TLS 1.3 برای داده‌های در حال انتقال، AES-256 برای داده‌های در حال استراحت و احراز هویت چندعاملی استفاده می‌کند.',
    ar: 'نأخذ أمن بياناتك على محمل الجد. تستخدم منصتنا تشفير TLS 1.3 للبيانات المنقولة وAES-256 للبيانات المخزنة.',
  },
  changes: {
    en: 'We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or industry standards. Material changes will be communicated via email.',
    fa: 'ممکن است این سیاست حریم خصوصی را گهگاه به‌روزرسانی کنیم. تغییرات مهم از طریق ایمیل اعلام خواهد شد.',
    ar: 'قد نقوم بتحديث سياسة الخصوصية هذه من وقت لآخر. سيتم الإبلاغ عن التغييرات الجوهرية عبر البريد الإلكتروني.',
  },
  contact: {
    en: 'If you have any questions regarding this Privacy Policy, please contact our Data Protection Officer at privacy@lexoya.com.',
    fa: 'اگر در مورد این سیاست حریم خصوصی سوالی دارید، با افسر حفاظت از داده‌های ما در privacy@lexoya.com تماس بگیرید.',
    ar: 'إذا كان لديك أي أسئلة بخصوص سياسة الخصوصية هذه، يرجى الاتصال بمسؤول حماية البيانات لدينا على privacy@lexoya.com.',
  },
}

const lastUpdated = 'January 1, 2025'

export default function PrivacyPage() {
  const lang = useLang()

  return (
    <>
      <section className="relative pt-32 pb-24 sm:pt-40 sm:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#4f46e5]/5 via-transparent to-transparent pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-[#6366f1]/15 rounded-full blur-[160px] pointer-events-none opacity-50" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-[#6366f1]/15 rounded-full blur-[140px] pointer-events-none opacity-75" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[300px] bg-[#6366f1]/10 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 inset-x-0 h-40 bg-gradient-to-t from-[#07070d] via-[#07070d]/80 to-transparent pointer-events-none z-10" />

        <div className="relative max-w-3xl mx-auto px-8 sm:px-8 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-sm font-medium text-[#818cf8] tracking-wide uppercase">{t(lang, privacyT.subtitle)}</span>
            <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight text-white">
              {t(lang, privacyT.heading)}
            </h1>
            <p className="mt-4 text-sm text-[#64748b]">{t(lang, privacyT.lastUpdated)} {lastUpdated}</p>
            <p className="mt-6 text-lg text-[#94a3b8] leading-relaxed">
              {t(lang, privacyT.intro)}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="relative py-24 sm:py-32 bg-white/[0.005]">
        <div className="absolute inset-0 dot-pattern pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#4f46e5]/[0.02] to-transparent pointer-events-none" />

        <div className="relative max-w-3xl mx-auto px-8 sm:px-8 lg:px-8">
          <div className="space-y-16">
            {sectionKeys.map((key, i) => {
              const content = sectionContent[key]
              const contentText = lang === 'fa' ? content.fa : lang === 'ar' ? content.ar : content.en
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                >
                  <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
                    {t(lang, privacyT.sections[key].title)}
                  </h2>
                  <p className="text-[#94a3b8] leading-relaxed">{contentText}</p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>
    </>
  )
}
